// ============================================================================
// QuestionnaireTrima — Main page for the Trima skills questionnaire
// ============================================================================
// Mounted at route ROUTES.TRIMA (/questionnaire-competences).
//
// Responsibilities:
//   - Verify eligibility on mount, redirect if not eligible
//   - Manage the phase state machine (invitation → ranking → cadran → thanks)
//   - Load ranking payload before showing the ranking phase
//   - Restore in-progress cadran state from encrypted localStorage if any
//
// To remove the entire Trima feature: delete this file and the entire
// pages/questionnaire-trima/ folder, plus the route entry, plus the small
// integrations tagged "=== TRIMA INTEGRATION ===" in:
//   - Questionnaire.tsx
//   - QuestionnaireEnd.tsx
//   - i18n.tsx
//   - constants/routes.ts
//   - The route registration in MobileLayout (or wherever it's registered)
// ============================================================================

import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDeviceSelectors } from 'react-device-detect';
import PageLayout from 'layout/page/Page.layout';
import { UserContext, UserContextProps } from 'contexts/UserContext';
import { ROUTES } from 'constants/routes';
import { checkTrimaEligibility } from 'pages/questionnaire-trima/eligibility';
import TrimaInvitation from 'pages/questionnaire-trima/slides/invitation/TrimaInvitation';
import TrimaRankingMain from 'pages/questionnaire-trima/slides/ranking/TrimaRankingMain';
import TrimaCadranMain from 'pages/questionnaire-trima/slides/cadran/TrimaCadranMain';
import TrimaThanks from 'pages/questionnaire-trima/slides/thanks/TrimaThanks';
import useAPITrima from 'hooks/api/trima/useAPITrima';
import { trimaStorage } from 'helpers/trima/trimaStorage';
import {
  TrimaPhase,
  TrimaRankingPayload,
  TrimaCadranPayload,
} from 'pages/questionnaire-trima/types/trima.types';

/**
 * TODO: when the BE provides a real way to know if ComColors is complete for a
 * given user (e.g. from user.profileInfo), wire it here. For now this is a
 * placeholder that assumes the user is eligible if they reach this route.
 *
 * The eligibility helper is already designed to consume `isComColorsComplete`
 * so the integration is one prop change away.
 */
const useIsComColorsComplete = (): boolean => {
  // Placeholder: trust that the user reached this route legitimately.
  // Replace with a real read from user.profileInfo when available.
  return true;
};

const QuestionnaireTrima: React.FC = () => {
  const { user } = useContext(UserContext) as UserContextProps;
  const { i18n } = useTranslation();
  const history = useHistory();
  const [selectors] = useDeviceSelectors(window.navigator.userAgent);
  const { isMobile } = selectors;
  const isComColorsComplete = useIsComColorsComplete();

  const { loadRanking } = useAPITrima();

  // Phase machine. Default to 'invitation' on fresh load.
  const [phase, setPhase] = useState<TrimaPhase>('invitation');

  useEffect(() => {
    (document.querySelector('ion-content') as HTMLIonContentElement | null)?.scrollToTop(0);
  }, [phase]);
  const [rankingPayload, setRankingPayload] = useState<TrimaRankingPayload | null>(null);
  const [cadranPayload, setCadranPayload] = useState<TrimaCadranPayload | null>(null);

  // ---------------------------------------------------------------------
  // Eligibility check on mount + on every user/lang/mobile change
  // ---------------------------------------------------------------------
  useEffect(() => {
    const elig = checkTrimaEligibility({
      user,
      language: i18n.language,
      isMobile,
      isComColorsComplete,
    });
    if (!elig.isEligible) {
      // Redirect based on the failing reason.
      switch (elig.reason) {
        case 'not-logged-in':
          history.replace(ROUTES.LOGIN);
          break;

        case 'not-a-valid-profile':
          history.replace(ROUTES.INDEX);
          break;
        case 'already-completed':
          history.replace(ROUTES.INDEX);
          break;
        case 'comcolors-incomplete':
          // Send them to the ComColors flow (rendered conditionally by
          // MainLayout when their profile flags are set).
          history.replace(ROUTES.INDEX);
          break;
        case 'not-mobile':
        case 'not-french':
        default:
          history.replace(ROUTES.INDEX);
          break;
      }
    }
  }, [user, i18n.language, isMobile, isComColorsComplete, history]);

  // ---------------------------------------------------------------------
  // Resume from persisted state on mount, if any
  // ---------------------------------------------------------------------
  useEffect(() => {
    const persisted = trimaStorage.load();
    if (!persisted) return;
    // TODO: implement proper resume once the BE exposes a GET endpoint to
    // re-fetch the current cadran's items for a given session_competence_id.
    // For now, wipe the stored state and let the user restart from the
    // invitation screen to avoid showing stale/mismatched cadran data.
    trimaStorage.clear();
    // eslint-disable-next-line
  }, []);

  // ---------------------------------------------------------------------
  // Phase transitions
  // ---------------------------------------------------------------------

  const startRanking = async () => {
    const payload = await loadRanking();
    if (!payload) return;
    setRankingPayload(payload);
    setPhase('ranking');
  };

  const handleRankingComplete = (firstCadranPayload: TrimaCadranPayload) => {
    setCadranPayload(firstCadranPayload);
    setPhase('cadran');
  };

  const handleCadranComplete = () => {
    trimaStorage.clear();
    setPhase('thanks');
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  return (
    <PageLayout
      headerProps={{ showLogo: true, hideNotification: true, showLangSwitcher: false }}
      className="page-layout-trima"
    >
      <div style={{
        maxWidth: 960,
        margin: '0 auto'
      }}>
        {phase === 'invitation' && (
          <TrimaInvitation
            onAccept={startRanking}
          // onRefuse default = clear storage + go home, which is what we want.
          />
        )}

        {phase === 'ranking' && rankingPayload && (
          <TrimaRankingMain
            payload={rankingPayload}
            onComplete={handleRankingComplete}
            onError={() => {
              // On POST failure: stay on the last question, let the user retry.
              // The toast was already shown by the hook.
            }}
          />
        )}

        {phase === 'cadran' && cadranPayload && (
          <TrimaCadranMain
            initialPayload={cadranPayload}
            initialState={null}
            onComplete={handleCadranComplete}
          />
        )}

        {phase === 'thanks' && <TrimaThanks />}
      </div>
    </PageLayout>
  );
};

export default QuestionnaireTrima;
